/* global expense_id parentName canBeEdited budgetIsLocked processAjaxConfirmation templateAssignUrl templateAssignUpdateUrl csrfToken templateAssignImportUrl */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';
import classNames from 'classnames';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

import BudgetTagsItemModal from '../budget_tags/BudgetTagsItemModal';

import AssignTable from './AssignTable';
import CommitmentModal from './CommitmentModal';
import ExpenseItem from './ExpenseItem';
import ExpenseItemShow from './ExpenseItemShow';

import Button from 'CommonComponents/Button';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';

import useModal from 'Hooks/useModal';
import { useToast } from 'Hooks/useToast';

import PlusCircleIcon from 'Assets/icons/plus-circle.svg';

function TemplateAssign(props) {
    const [latestResponse, setLatestResponse] = useState({});
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isLoaded, setIsLoaded] = useState(false);
    const [venues, setVenues] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [maxQuantitiesColumns, setMaxQuantitiesColumns] = useState(2);
    const [maximumAmount, setMaximumAmount] = useState(null);
    const [amountLeft, setAmountLeft] = useState(null);
    const [projectId, setProjectId] = useState(null);
    const [projectAmounts, setProjectAmounts] = useState(null);
    const [expenseTransaction, setExpenseTransaction] = useState(null);
    const [showExpensesTab, setShowExpensesTab] = useState(true);
    const [showAssignTab, setShowAssignTab] = useState(true);
    const [activeTab, setActiveTab] = useState(showExpensesTab ? 'expenses' : 'assign');
    const [totals, setTotals] = useState({});
    const [tableHeaders, setTableHeaders] = useState('venues');
    const [expenseGroups, setExpenseGroups] = useState({});
    const [showExpense, setShowExpense] = useState([]);
    const [reloadPage, setReloadPage] = useState(props.reloadPage != undefined ? props.reloadPage : true);
    const [committedTotals, setCommittedTotals] = useState({});
    const [committedExpensables, setCommittedExpensables] = useState(null);
    const [commitmentsExists, setCommitmentsExists] = useState(false);
    const commitmentModal = useModal();
    const [finalStates, setFinalStates] = useState({});
    const [quantityLinkStates, setQuantityLinkStates] = useState({});
    // Quantity links chosen for not-yet-saved template children. Keyed by
    // [expenseKey][venueId][slotIndex]; applied server-side after save.
    const [deferredQuantityLinks, setDeferredQuantityLinks] = useState({});

    const editableTable = props.editable === 'true';

    // Item tags save on their own endpoint, so they are held apart from `expenses` and never posted by save().
    const showBudgetTags = !!props.budgetTagsUrl;
    const [budgetTagCells, setBudgetTagCells] = useState({});
    const [budgetTagsEditable, setBudgetTagsEditable] = useState(false);
    const [budgetTagsTarget, setBudgetTagsTarget] = useState(null);
    const openBudgetTags = useCallback((id, name) => setBudgetTagsTarget({ id, name }), []);
    const closeBudgetTags = useCallback(() => setBudgetTagsTarget(null), []);
    const budgetTagsSaved = useCallback((id, cell) => setBudgetTagCells((cells) => ({ ...cells, [id]: cell })), []);
    const expensesListRef = useRef();
    // Remembers which cell opened the deferred picker so quantitylink:selected
    // (which carries only the source, not the target cell) can be routed back.
    const deferredPickerTargetRef = useRef(null);

    const toast = useToast();

    // Toggle a single selection independently. Each template child / venue cell /
    // top-level venue is selected or unselected on its own — toggling one must NEVER
    // mutate the selection of any sibling. (A previous "one at a time" radio variant
    // caused unselecting one template item to silently unselect the others, which in
    // turn dropped those children from the procurement on save — see IB-635.)
    const toggleFinalState = (targetType, venueId = null, expenseId = null) => {
        if (targetType === 'expense') {
            setFinalStates((state) => {
                if (!state?.expenses?.[expenseId]) {
                    return state;
                }

                return {
                    ...state,
                    expenses: {
                        ...state.expenses,
                        [expenseId]: {
                            ...state.expenses[expenseId],
                            isSelected: !state.expenses[expenseId].isSelected,
                        },
                    },
                };
            });
        } else if (targetType === 'venue') {
            setFinalStates((state) => {
                const currentVenueState = state?.expenses?.[expenseId]?.venues?.[venueId];
                if (!currentVenueState) {
                    return state;
                }

                return {
                    ...state,
                    expenses: {
                        ...state.expenses,
                        [expenseId]: {
                            ...state.expenses[expenseId],
                            venues: {
                                ...state.expenses[expenseId].venues,
                                [venueId]: {
                                    ...currentVenueState,
                                    isSelected: !currentVenueState.isSelected,
                                },
                            },
                        },
                    },
                };
            });
        } else {
            setFinalStates((state) => {
                if (!state?.venues?.[venueId]) {
                    return state;
                }

                return {
                    ...state,
                    venues: {
                        ...state.venues,
                        [venueId]: {
                            ...state.venues[venueId],
                            isSelected: !state.venues[venueId].isSelected,
                        },
                    },
                };
            });
        }
    };

    const showCommitmentModal = (committed_expenditures, venue_id, expensable_id = null) => {
        if (expensable_id != null) {
            setCommittedExpensables(getCommittedExpensables(committed_expenditures, expensable_id, venue_id));
            commitmentModal.show();
        }
    };

    const closeModal = () => {
        setCommittedExpensables(null);
        commitmentModal.hide();
    };

    function getCommittedExpensables(committed_expenditures, expensable_id, venue_id) {
        const result = [];
        if (Array.isArray(committed_expenditures)) {
            for (const expenditure of committed_expenditures) {
                for (let expensable of expenditure.committed_expensables) {
                    if (expensable.expensable_id === expensable_id && expensable.venue_id === venue_id) {
                        let selectedExpensable = expenses.find((expense) => expense.id === expenditure.expense_id);
                        expensable.price = selectedExpensable?.price;
                        for (let q in expensable.amount_data) {
                            if (Object.prototype.hasOwnProperty.call(expensable.amount_data, q)) {
                                if ('Price' === q) {
                                    expensable.price = parseFloat(expensable.amount_data[q]);
                                }
                            }
                        }
                        result.push(expensable);
                    }
                }
            }
        }

        return result;
    }

    const amountSum = (amount, quantities) => {
        let totals = { count: 1 };
        for (let i = 0; i < quantities.length; i++) {
            const quant = quantities[i];
            if (!Object.prototype.hasOwnProperty.call(amount, quant.name) || !amount[quant.name]) {
                amount[quant.name] = 0;
            }

            if ('Price' !== quant.name) {
                totals.count *= parseFloat(amount[quant.name]);
            }
        }
        for (let q in amount) {
            if (Object.prototype.hasOwnProperty.call(amount, q)) {
                if ('Price' === q) {
                    totals.price = parseFloat(amount[q]);
                }
            }
        }
        return totals;
    };

    const handleRotate = useCallback(() => {
        setTableHeaders((oldDirection) => {
            return 'venues' === oldDirection ? 'expenses' : 'venues';
        });
    });

    const handleDeleteExpense = useCallback((expenseKey) => {
        const deletedExp = expenses[expenseKey];
        if (deletedExp.type === 'template_divider') {
            toggleVisibility(deletedExp, true);
            const groups = { ...expenseGroups };
            delete groups[deletedExp.id];
            setExpenseGroups(groups);
        }

        setExpenses((oldExpenses) => {
            let newExpenses = [...oldExpenses];
            newExpenses[expenseKey].deleted = true;
            return newExpenses;
        });
    });
    useEffect(() => {
        if ('expenses' === activeTab) {
            if (expensesListRef.current) {
                expensesListRef.current.scrollTop = expensesListRef.current.scrollHeight;
            }
        }
    }, [expenses.length]);
    const handleSaveAssigns = useCallback((e) => {
        if (budgetIsLocked) {
            processAjaxConfirmation(save);
        } else {
            save();
        }
    });

    const addNewGroupFromExpense = (expense) => {
        if (expense.type === 'template_divider' && !(expense.id in expenseGroups)) {
            const groups = { ...expenseGroups };
            groups[expense.id] = true;
            setExpenseGroups(groups);
        }
    };

    useEffect(() => {
        (async () => {
            await processData(latestResponse);
            setIsLoaded(true);
        })();
    }, [latestResponse]);

    const processData = (latestResponse) => {
        if (Object.prototype.hasOwnProperty.call(latestResponse, 'data')) {
            let committedAmountTotals = {};
            if (Object.prototype.hasOwnProperty.call(latestResponse.data, 'commitmentsExists')) {
                setCommitmentsExists(latestResponse.data.commitmentsExists);
            }
            if (Object.prototype.hasOwnProperty.call(latestResponse.data, 'committedTotals')) {
                committedAmountTotals = latestResponse.data.committedTotals;
            }
            let initialTotals = {
                committed: { grand: Object.prototype.hasOwnProperty.call(committedAmountTotals, 'total') ? committedAmountTotals.total : 0 },
                uncommitted: { grand: 0 },
                budget: { grand: Object.prototype.hasOwnProperty.call(committedAmountTotals, 'total') ? committedAmountTotals.total : 0 },
            };
            setShowExpensesTab(Object.prototype.hasOwnProperty.call(latestResponse.data, 'showExpensesTab') ? latestResponse.data.showExpensesTab : false);
            setShowAssignTab(Object.prototype.hasOwnProperty.call(latestResponse.data, 'showAssignTab') ? latestResponse.data.showAssignTab : true);
            setCommittedTotals(Object.prototype.hasOwnProperty.call(latestResponse.data, 'committedTotals') ? latestResponse.data.committedTotals : {});
            setFinalStates(Object.prototype.hasOwnProperty.call(latestResponse.data, 'states') ? latestResponse.data.states : {});
            if (Object.prototype.hasOwnProperty.call(latestResponse.data, 'budgetTags')) {
                setBudgetTagCells(latestResponse.data.budgetTags || {});
                setBudgetTagsEditable(!!latestResponse.data.budgetTagsEditable);
            }
            if (Object.prototype.hasOwnProperty.call(latestResponse.data, 'venues')) {
                setVenues(latestResponse.data.venues);
                initialTotals.venues = {};
            }
            if (Object.prototype.hasOwnProperty.call(latestResponse.data, 'expenses')) {
                setExpenses(latestResponse.data.expenses);
                initialTotals.expenses = [];

                const showed = [];
                const groups = {};
                const expensesCommittedTotals = Object.prototype.hasOwnProperty.call(committedAmountTotals, 'expenses') ? committedAmountTotals.expenses : {};
                // initialTotals.venues = [];
                for (let i = 0; i < latestResponse.data.expenses.length; i++) {
                    const expense = latestResponse.data.expenses[i];
                    const expenseCommittedTotal = Object.prototype.hasOwnProperty.call(expensesCommittedTotals, expense.id)
                        ? expensesCommittedTotals[expense.id]
                        : {};
                    showed[i] = true;
                    if (expense.type === 'template_divider') {
                        groups[expense.id] = true;
                    }
                    expense.amount = expense.amount.length ? expense.amount : 0;
                    initialTotals.expenses.push({
                        id: expense.id,
                        price: expense.price,
                        venues: {},
                        committed: {
                            total: Object.prototype.hasOwnProperty.call(expenseCommittedTotal, 'total') ? expenseCommittedTotal.total : 0,
                            count: Object.prototype.hasOwnProperty.call(expenseCommittedTotal, 'quantity') ? expenseCommittedTotal.quantity : 0,
                        },
                        uncommitted: {
                            total: 0,
                            count: 0,
                        },
                        budget: {
                            total: Object.prototype.hasOwnProperty.call(expenseCommittedTotal, 'total') ? expenseCommittedTotal.total : 0,
                            count: Object.prototype.hasOwnProperty.call(expenseCommittedTotal, 'quantity') ? expenseCommittedTotal.quantity : 0,
                        },
                    });
                    if (expense.venues) {
                        const venuesCommittedTotals = Object.prototype.hasOwnProperty.call(expenseCommittedTotal, 'venues') ? expenseCommittedTotal.venues : {};
                        for (let j = 0; j < expense.venues.length; j++) {
                            let venue = expense.venues[j];
                            const venueCommittedTotal = Object.prototype.hasOwnProperty.call(venuesCommittedTotals, venue.id)
                                ? venuesCommittedTotals[venue.id]
                                : {};
                            const amount = JSON.parse(venue.pivot.uncommitted_amount);
                            let amountDetails = amountSum(amount, expense.amount);
                            let venuePrice = Object.prototype.hasOwnProperty.call(amountDetails, 'price') ? amountDetails.price : expense.price;
                            let amountCount = amountDetails.count;
                            let amountTotal = amountCount * venuePrice;
                            initialTotals.expenses[i].uncommitted.count += amountCount;
                            initialTotals.expenses[i].uncommitted.total += amountTotal;
                            initialTotals.expenses[i].budget.count = initialTotals.expenses[i].uncommitted.count + initialTotals.expenses[i].committed.count;
                            initialTotals.expenses[i].budget.total = initialTotals.expenses[i].uncommitted.total + initialTotals.expenses[i].committed.total;
                            initialTotals.expenses[i].venues[venue.id] = {
                                price: venuePrice,
                                committed: {
                                    total: Object.prototype.hasOwnProperty.call(venueCommittedTotal, 'total') ? venueCommittedTotal.total : 0,
                                    count: Object.prototype.hasOwnProperty.call(venueCommittedTotal, 'quantity') ? venueCommittedTotal.quantity : 0,
                                },
                                uncommitted: {
                                    count: amountCount,
                                    total: amountCount * venuePrice,
                                },
                                budget: {
                                    total:
                                        (Object.prototype.hasOwnProperty.call(venueCommittedTotal, 'total') ? venueCommittedTotal.total : 0) +
                                        amountCount * venuePrice,
                                    count:
                                        (Object.prototype.hasOwnProperty.call(venueCommittedTotal, 'quantity') ? venueCommittedTotal.quantity : 0) +
                                        amountCount,
                                },
                            };
                            initialTotals.uncommitted.grand += amountTotal;
                            initialTotals.budget.grand = initialTotals.uncommitted.grand + initialTotals.committed.grand;

                            let venueCommitted = Object.prototype.hasOwnProperty.call(venueCommittedTotal, 'total') ? venueCommittedTotal.total : 0;
                            let venueBudget = amountTotal + venueCommitted;
                            if (!Object.prototype.hasOwnProperty.call(initialTotals.venues, venue.id)) {
                                initialTotals.venues[venue.id] = {
                                    committed: venueCommitted,
                                    uncommitted: amountTotal,
                                    budget: venueBudget,
                                };
                            } else {
                                initialTotals.venues[venue.id].committed += venueCommitted;
                                initialTotals.venues[venue.id].uncommitted += amountTotal;
                                initialTotals.venues[venue.id].budget += venueBudget;
                            }
                        }
                    }
                }
                setExpenseGroups(groups);
                setShowExpense(showed);
            }
            if (Object.prototype.hasOwnProperty.call(latestResponse.data, 'maxQuantitiesColumns')) {
                setMaxQuantitiesColumns(latestResponse.data.maxQuantitiesColumns);
            }
            if (Object.prototype.hasOwnProperty.call(latestResponse.data, 'amount_left')) {
                setMaximumAmount(initialTotals.uncommitted.grand + latestResponse.data.amount_left);
                setAmountLeft(latestResponse.data.amount_left);
            }
            if (Object.prototype.hasOwnProperty.call(latestResponse.data, 'project_id')) {
                setProjectId(latestResponse.data.project_id);
            }
            if (Object.prototype.hasOwnProperty.call(latestResponse.data, 'project_amounts')) {
                setProjectAmounts(latestResponse.data.project_amounts);
            }
            if (Object.prototype.hasOwnProperty.call(latestResponse.data, 'transaction')) {
                setExpenseTransaction(latestResponse.data.transaction);
            }
            if (Object.prototype.hasOwnProperty.call(latestResponse.data, 'quantityLinkStates')) {
                setQuantityLinkStates(latestResponse.data.quantityLinkStates);
            }

            setTotals(initialTotals);
        }
    };

    const handleAddExpense = useCallback((type) => {
        setExpenses((oldExpenses) => {
            let newExpenses = [...oldExpenses];
            const defaultAmount = [];

            const exp = {
                id: 'new-' + (Math.random() + 1).toString(36).substring(7),
                name: 'template_child' === type ? 'New Expense' : 'Divider',
                description: null,
                price: '0',
                type: type,
                venues: [],
                amount: defaultAmount,
            };

            newExpenses.push(exp);
            addNewGroupFromExpense(exp);
            const showed = [...showExpense];
            showed.push(true);
            setShowExpense(showed);

            return newExpenses;
        });
        setTotals((oldTotals) => {
            let newTotals = { ...oldTotals };
            newTotals.expenses.push({
                id: '',
                price: '0',
                venues: {},
                committed: {
                    total: 0,
                    count: 0,
                },
                uncommitted: {
                    total: 0,
                    count: 0,
                },
                budget: {
                    total: 0,
                    count: 0,
                },
            });
            return oldTotals;
        });
    });

    const updateExpense = useCallback((value, arrayKey, propName, propNameSecond = '', index = null) => {
        let newValue = value;
        setExpenses((expenses) => {
            let newExpenses = [...expenses];
            if (propNameSecond === '') {
                newExpenses[arrayKey][propName] = newValue;
            } else {
                if (propName === 'amount' && index !== null) {
                    newExpenses[arrayKey][propName][index][propNameSecond] = newValue;
                    newExpenses[arrayKey].venues.map((v, i) => {
                        var oldAssigns = JSON.parse(v.pivot.uncommitted_amount);

                        // Convert the object to an array of key-value pairs
                        let entries = Object.entries(oldAssigns);

                        // Modify the key at index
                        if (entries[index]) {
                            entries[index][0] = newValue; // Change the key
                        }

                        // Convert the entries back to an object
                        oldAssigns = Object.fromEntries(entries);

                        // Assign to back to pivot
                        newExpenses[arrayKey].venues[i].pivot.uncommitted_amount = JSON.stringify(oldAssigns);
                    });
                } else {
                    newExpenses[arrayKey][propName][propNameSecond] = newValue;
                }
            }

            return newExpenses;
        });
        setTotals((oldTotals) => {
            let newTotals = { ...oldTotals };
            newTotals.expenses[arrayKey][propName] = newValue;
            if (propName === 'price') {
                newTotals.expenses[arrayKey].uncommitted.total = newValue * newTotals.expenses[arrayKey].uncommitted.count;
                newTotals.expenses[arrayKey].budget.total = newTotals.expenses[arrayKey].uncommitted.total + newTotals.expenses[arrayKey].committed.total;
                const exp = newTotals.expenses[arrayKey];
                Object.keys(exp.venues).forEach((id) => {
                    exp.venues[id].price = newValue;
                    let oldVenueTotal = exp.venues[id].uncommitted.total;
                    const newVenueTotal = newValue * exp.venues[id].uncommitted.count;
                    const diff = newVenueTotal - oldVenueTotal;

                    exp.venues[id].uncommitted.total = newVenueTotal;
                    exp.venues[id].budget.total = exp.venues[id].uncommitted.total + exp.venues[id].committed.total;

                    Object.keys(newTotals.venues).forEach((key) => {
                        if (key === id) {
                            newTotals.venues[key].uncommitted += diff;
                            newTotals.venues[key].budget = newTotals.venues[key].uncommitted + newTotals.venues[key].committed;
                        }
                    });
                    newTotals.uncommitted.grand += diff;
                    newTotals.budget.grand = newTotals.uncommitted.grand + newTotals.committed.grand;
                });
            }

            return newTotals;
        });
    }, []);

    const handleTotalsChange = useCallback((expenseKey, venueId, newAssigns, newExpenses) => {
        setTotals((oldTotals) => {
            let amountDetails = amountSum(newAssigns, newExpenses[expenseKey].amount);
            let amountCount = amountDetails.count;
            let amountTotal =
                amountCount * (Object.prototype.hasOwnProperty.call(amountDetails, 'price') ? amountDetails.price : oldTotals.expenses[expenseKey].price);
            let newTotals = { ...oldTotals };
            let venuePrice = Object.prototype.hasOwnProperty.call(amountDetails, 'price') ? amountDetails.price : oldTotals.expenses[expenseKey].price;
            if (!Object.prototype.hasOwnProperty.call(newTotals.expenses[expenseKey].venues, venueId)) {
                newTotals.expenses[expenseKey].venues[venueId] = {
                    price: venuePrice,
                    committed: {
                        count: 0,
                        total: 0,
                    },
                    uncommitted: {
                        count: 0,
                        total: 0,
                    },
                    budget: {
                        count: 0,
                        total: 0,
                    },
                };
            }
            let uncommittedCountChange = newTotals.expenses[expenseKey].venues[venueId].uncommitted.count - amountCount;
            let uncommittedTotalChange = newTotals.expenses[expenseKey].venues[venueId].uncommitted.total - amountTotal;
            if (!Object.prototype.hasOwnProperty.call(oldTotals.venues, venueId)) {
                newTotals.venues[venueId] = {
                    committed: 0,
                    uncommitted: 0,
                    budget: 0,
                };
            }
            newTotals.venues[venueId].uncommitted -= uncommittedTotalChange;
            newTotals.expenses[expenseKey].uncommitted.count -= uncommittedCountChange;
            newTotals.expenses[expenseKey].uncommitted.total -= uncommittedTotalChange;
            newTotals.expenses[expenseKey].venues[venueId].uncommitted.count -= uncommittedCountChange;
            newTotals.expenses[expenseKey].venues[venueId].uncommitted.total -= uncommittedTotalChange;
            newTotals.uncommitted.grand -= uncommittedTotalChange;

            newTotals.venues[venueId].budget = newTotals.venues[venueId].uncommitted + newTotals.venues[venueId].committed;
            newTotals.expenses[expenseKey].budget.count = newTotals.expenses[expenseKey].uncommitted.count + newTotals.expenses[expenseKey].committed.count;
            newTotals.expenses[expenseKey].budget.total = newTotals.expenses[expenseKey].uncommitted.total + newTotals.expenses[expenseKey].committed.total;
            newTotals.expenses[expenseKey].venues[venueId].budget.count =
                newTotals.expenses[expenseKey].venues[venueId].uncommitted.count + newTotals.expenses[expenseKey].venues[venueId].committed.count;
            newTotals.expenses[expenseKey].venues[venueId].budget.total =
                newTotals.expenses[expenseKey].venues[venueId].uncommitted.total + newTotals.expenses[expenseKey].venues[venueId].committed.total;
            newTotals.budget.grand = newTotals.uncommitted.grand + newTotals.committed.grand;

            return newTotals;
        });
    }, []);

    const handleQuantityChange = useCallback((value, expenseKey, venueId, quantity, expensePrice) => {
        setExpenses((expenses) => {
            let newExpenses = [...expenses];
            var existingVenue = newExpenses[expenseKey].venues.find((v) => v.id == venueId);
            let newAmount;
            if (typeof existingVenue !== 'undefined') {
                var oldAssigns = JSON.parse(existingVenue.pivot.uncommitted_amount);
                newAmount = { ...oldAssigns, [quantity]: value };
                if ('Price' === quantity && !value) {
                    delete newAmount.Price;
                }
                existingVenue.pivot.uncommitted_amount = JSON.stringify(newAmount);
            } else {
                newAmount = { [quantity]: value };
                if ('Price' === quantity && !value) {
                    delete newAmount.Price;
                }
                newExpenses[expenseKey].venues.push({
                    id: venueId,
                    pivot: {
                        uncommitted_amount: JSON.stringify(newAmount),
                    },
                });
            }
            handleTotalsChange(expenseKey, venueId, newAmount, newExpenses);
            newExpenses[expenseKey].name = expenses[expenseKey].name;
            return newExpenses;
        });
    }, []);

    // Open the source picker in deferred mode for an unsaved template-child cell.
    const openDeferredQuantityPicker = useCallback((target) => {
        deferredPickerTargetRef.current = target;
        const current = target.current || null;
        window.dispatchEvent(
            new CustomEvent('quantitylink:open', {
                detail: {
                    deferred: true,
                    sourcesUrl: window.quantityLinkCreateSourcesUrl,
                    amountSlotIndex: target.slotIndex,
                    venueId: target.venueId,
                    currentLink: current ? { sourceType: current.sourceType, sourceId: current.sourceId, driver: current.driver ?? null } : null,
                },
            }),
        );
    }, []);

    // Drop a pending (unsaved) deferred link and re-enable manual entry.
    const clearDeferredQuantityLink = useCallback(
        (target) => {
            setDeferredQuantityLinks((prev) => {
                const next = { ...prev };
                const byVenue = { ...(next[target.expenseKey] || {}) };
                const bySlot = { ...(byVenue[target.venueId] || {}) };
                delete bySlot[target.slotIndex];
                if (Object.keys(bySlot).length) {
                    byVenue[target.venueId] = bySlot;
                } else {
                    delete byVenue[target.venueId];
                }
                if (Object.keys(byVenue).length) {
                    next[target.expenseKey] = byVenue;
                } else {
                    delete next[target.expenseKey];
                }
                return next;
            });
            if (target.quantityName) {
                handleQuantityChange(0, target.expenseKey, target.venueId, target.quantityName, expenses[target.expenseKey]?.price);
            }
        },
        [handleQuantityChange, expenses],
    );

    useEffect(() => {
        const handleSelected = (e) => {
            const target = deferredPickerTargetRef.current;
            deferredPickerTargetRef.current = null;
            if (!target) return;
            const sel = e.detail || {};
            setDeferredQuantityLinks((prev) => {
                const next = { ...prev };
                const byVenue = { ...(next[target.expenseKey] || {}) };
                const bySlot = { ...(byVenue[target.venueId] || {}) };
                bySlot[target.slotIndex] = {
                    sourceType: sel.sourceType,
                    sourceId: sel.sourceId,
                    driver: sel.driver ?? null,
                    label: sel.label,
                    value: sel.value,
                };
                byVenue[target.venueId] = bySlot;
                next[target.expenseKey] = byVenue;
                return next;
            });
            // Reflect the live source value in the cell + totals straight away.
            if (target.quantityName) {
                handleQuantityChange(sel.value ?? 0, target.expenseKey, target.venueId, target.quantityName, expenses[target.expenseKey]?.price);
            }
        };
        window.addEventListener('quantitylink:selected', handleSelected);
        return () => window.removeEventListener('quantitylink:selected', handleSelected);
    }, [handleQuantityChange, expenses]);

    function handleOnDragEnd(result) {
        if (!result.destination) return;
        setExpenses((expenses) => {
            const newExpenses = [...expenses];
            const [reorderedExpense] = newExpenses.splice(result.source.index, 1);
            newExpenses.splice(result.destination.index, 0, reorderedExpense);
            return newExpenses;
        });
        setTotals((totals) => {
            const newExpenses = [...totals.expenses];
            const [reorderedExpense] = newExpenses.splice(result.source.index, 1);
            newExpenses.splice(result.destination.index, 0, reorderedExpense);
            totals.expenses = newExpenses;
            return totals;
        });
    }

    const fetchData = () => {
        fetch(props.templateAssignUrl ?? templateAssignUrl)
            .then((res) => res.json())
            .then(
                (result) => {
                    setLatestResponse(result);
                    setLoading(false);
                },
                (error) => {
                    setError(error);
                    setIsLoaded(true);
                },
            );
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        const handleLinkChanged = () => fetchData();
        window.addEventListener('quantitylink:changed', handleLinkChanged);
        return () => window.removeEventListener('quantitylink:changed', handleLinkChanged);
    }, []);

    useEffect(() => {
        setActiveTab(showExpensesTab ? 'expenses' : 'assign');
    }, [showExpensesTab]);

    function save() {
        let projectIncrease = null;
        if (maximumAmount !== null) {
            if (totals.uncommitted.grand > maximumAmount) {
                let over_budget = totals.uncommitted.grand - maximumAmount;
                let message = $t(
                    'You are requesting more {{over_budget_type}} budget than was initially allocated in the project - proceed and increase the overall project budget? (+{{over_budget_amount}})',
                    {
                        over_budget_type: expenseTransaction === 'cash' ? expenseTransaction : 'VIK',
                        over_budget_amount: over_budget,
                    },
                );
                let confirmation = confirm(message);
                if (!confirmation) {
                    return;
                }
                projectIncrease = {
                    project_id: projectId,
                    project_amounts: projectAmounts,
                    transaction: expenseTransaction,
                    over_budget: over_budget,
                    amount_left: amountLeft,
                };
            }
        }
        setIsLoaded(false);
        var expensesToSave = {};
        for (let i = 0; i < expenses.length; i++) {
            const exp = expenses[i];
            if (Object.prototype.hasOwnProperty.call(exp, 'deleted') && exp.deleted && !exp.id) {
                continue;
            }
            let newId = exp.id && exp.id.toString().indexOf('new-') === -1 ? exp.id : 'new-' + i;

            expensesToSave[newId] = { sync: {} };
            if (!exp.id || exp.id.toString().indexOf('new-') !== -1) {
                expensesToSave[newId].type = exp.type;
            }
            if (i != exp.template_order) {
                expensesToSave[newId].template_order = i;
            }
            // the next two line should have a check for change, and not always be set
            expensesToSave[newId].name = exp.name;
            expensesToSave[newId].description = exp.description;
            expensesToSave[newId].price = exp.price;
            expensesToSave[newId].amount = exp.amount?.length ? exp.amount.map((v, i) => ({ ...v, name: v.name || `Quantity ${i + 1}` })) : exp.amount;
            if (Object.prototype.hasOwnProperty.call(exp, 'deleted') && exp.deleted) {
                expensesToSave[newId].deleted = true;
            } else if (exp.venues) {
                for (let j = 0; j < exp.venues.length; j++) {
                    const ven = exp.venues[j];
                    expensesToSave[newId].sync[ven.id] = { uncommitted_amount: ven.pivot.uncommitted_amount };
                }
            }

            // Carry deferred quantity links so they are applied after the child is created.
            const expDeferred = deferredQuantityLinks[i];
            if (expDeferred && !(Object.prototype.hasOwnProperty.call(exp, 'deleted') && exp.deleted)) {
                const payload = {};
                Object.keys(expDeferred).forEach((venueId) => {
                    const bySlot = expDeferred[venueId];
                    Object.keys(bySlot).forEach((slotIndex) => {
                        const sel = bySlot[slotIndex];
                        if (!sel || (!sel.sourceId && !sel.driver)) return;
                        if (!payload[venueId]) payload[venueId] = {};
                        payload[venueId][slotIndex] = {
                            source_type: sel.sourceType,
                            source_id: sel.sourceId,
                            amount_slot_index: Number(slotIndex),
                            ...(sel.driver ? { driver: sel.driver } : {}),
                        };
                    });
                });
                if (Object.keys(payload).length) {
                    expensesToSave[newId].deferred_quantity_links = payload;
                }
            }
        }
        fetch(props.templateAssignUpdateUrl ?? templateAssignUpdateUrl, {
            method: props.templateAssignUpdateUrlMethod ?? 'POST',
            headers: {
                Accept: 'application/json',
                'X-CSRF-TOKEN': csrfToken,
            },
            body: JSON.stringify({
                expensesToSave: expensesToSave,
                projectIncrease: projectIncrease,
                finalStates: finalStates,
            }),
        })
            .then((res) => {
                if (!res.ok) {
                    // A refusal states its reason, which is shown as any other error is.
                    return res
                        .json()
                        .catch(() => ({}))
                        .then((body) => {
                            if (body?.error) {
                                return { error: body.error };
                            }
                            throw new Error('save_request_failed');
                        });
                }
                return res.json();
            })
            .then(
                (result) => {
                    if (result.message) {
                        alert(result.message);
                        fetchData();
                        if (reloadPage) {
                            window.location.reload();
                        }
                    } else if (result.error) {
                        toast.error(result.error);
                        fetchData();

                        return;
                    } else {
                        toast.success(
                            $t('The updates were successful. Cashflows are being updated in the background and may take several minutes to complete'),
                        );
                        setLatestResponse(result);
                    }
                    if (reloadPage && !props.successHandler) {
                        window.location.reload();
                    }
                    if (props.successHandler) {
                        props.successHandler(result);
                    }
                },
                () => {
                    // The save may have failed on the client (timeout / dropped connection) while
                    // still committing on the server. Rather than re-enable Save on the stale
                    // in-memory rows — which still carry temporary `new-*` ids and would be
                    // re-created (duplicates) or deleted as a no-op on retry — refetch the
                    // authoritative template state. Committed rows come back with real ids, so a
                    // retry updates them and a delete targets a real id. The grid stays locked in
                    // its loading state until fetchData resolves and flips isLoaded back on.
                    toast.error($t('Saving is taking longer than expected, please try again'));
                    fetchData();
                },
            );
    }

    const handleAddQuantityColumn = (expenseIndex) => {
        setExpenses((oldExpenses) => {
            let newExpenses = [...oldExpenses];
            newExpenses[expenseIndex].amount = [...(newExpenses[expenseIndex].amount || []), { name: '' }];
            return newExpenses;
        });
    };

    const handleDeleteQuantityColumn = (expenseIndex, quantityIndex) => {
        setExpenses((oldExpenses) => {
            let newExpenses = [...oldExpenses];
            newExpenses[expenseIndex].amount.splice(quantityIndex, 1);
            return newExpenses;
        });
    };

    const handleClick = (expense) => {
        toggleVisibility(expense);
    };

    const toggleVisibility = (expense, show = false) => {
        if (expense.type === 'template_divider') {
            let startIndex = -1;
            let endIndex = -1;

            for (let i = 0; i < expenses.length; i++) {
                const exp = expenses[i];
                if (startIndex === -1) {
                    startIndex = exp.id === expense.id ? i : startIndex;
                } else if (endIndex === -1) {
                    if (exp.type === 'template_divider' && !exp.deleted) {
                        endIndex = i;
                        break;
                    }
                }
            }

            endIndex = endIndex === -1 ? expenses.length : endIndex;
            const showed = [...showExpense];

            const initVal = showExpense[startIndex + 1];
            const val = show || !initVal;

            for (let i = 0; i < showed.length; i++) {
                if (i > startIndex && i < endIndex) {
                    showed[i] = val;
                }
            }

            setShowExpense(showed);
        }
    };

    if (error) {
        const rawMessage = error?.message ?? '';
        const isUnreadableResponse =
            rawMessage.includes('JSON') || rawMessage.includes('Unexpected token') || rawMessage.includes('did not match the expected pattern');
        const message = isUnreadableResponse ? $t('Saving is taking longer than expected, please try again') : rawMessage;
        return <div>{$t('Error {{message}}', { message })}</div>;
    } else if (loading) {
        return <LoadingSpinner />;
    } else if (!isLoaded) {
        return <div>{$t('Assigning expense - please wait. This can take a little while.')}</div>;
    } else if (!venues.length && !showExpensesTab) {
        return <div>{$t('No venues set')}</div>;
    } else {
        return (
            <div className="tw-space-y-6">
                <div className="in-nav horizontal" role="tablist">
                    {showExpensesTab && (
                        <button
                            className={('expenses' === activeTab ? 'active' : 'inactive') + ' in-nav__item'}
                            onClick={(e) => {
                                e.preventDefault();
                                setActiveTab('expenses');
                            }}
                        >
                            {$t('Manage Template')}
                        </button>
                    )}
                    {showAssignTab && (
                        <button
                            className={('assign' === activeTab ? 'active' : 'inactive') + ' in-nav__item'}
                            onClick={(e) => {
                                e.preventDefault();
                                setActiveTab('assign');
                            }}
                        >
                            {editableTable ? $t('Assign to venues') : $t('View assignment to venues')}
                        </button>
                    )}

                    {editableTable && props.canBeEdited && (
                        <div className="!tw-ml-auto">
                            <Button variant="primary" type="button" onClick={handleSaveAssigns}>
                                {$t('Save assigns')}
                            </Button>
                        </div>
                    )}
                </div>
                <div className="inbud-card">
                    <div className="tab-content">
                        {showExpensesTab && (
                            <div className={classNames('tab-pane fade p-0 stacked-panes border-0 h-100 mh-100', { 'active show': 'expenses' === activeTab })}>
                                <div>
                                    {editableTable && props.canBeEdited && (
                                        <div className="tw-flex tw-items-center tw-space-x-2 tw-mb-6">
                                            <Button small variant="primary" type="button" onClick={(e) => handleAddExpense('template_divider')}>
                                                <PlusCircleIcon className="tw-w-3.5 tw-h-3.5" />
                                                {$t('Add Divider')}
                                            </Button>
                                            <Button small variant="primary" type="button" onClick={(e) => handleAddExpense('template_child')}>
                                                <PlusCircleIcon className="tw-w-3.5 tw-h-3.5" />
                                                {$t('Add Expense')}
                                            </Button>
                                        </div>
                                    )}

                                    <div className="tw-overflow-y-auto tw-overflow-x-auto tw-border-2 tw-border-grey tw-rounded-lg tw-text-charcoal tw-text-sm">
                                        <div className="tw-sticky tw-top-0 tw-flex tw-items-end tw-font-semibold tw-bg-blue-25">
                                            <div className="tw-basis-12 tw-shrink-0 tw-p-3"></div>
                                            <div className="tw-basis-full tw-flex tw-items-end">
                                                <div className={classNames(showBudgetTags ? 'tw-basis-2/12' : 'tw-basis-3/12', 'tw-p-3')}>{$t('Name')}</div>
                                                <div className={classNames(showBudgetTags ? 'tw-basis-2/12' : 'tw-basis-3/12', 'tw-p-3')}>
                                                    {$t('Description')}
                                                </div>
                                                <div className="tw-basis-2/12 tw-p-3">{$t('Price')}</div>
                                                <div className="tw-basis-4/12 tw-grid tw-grid-cols-2">
                                                    {!!maxQuantitiesColumns &&
                                                        [...Array(maxQuantitiesColumns).keys()].map((index) => (
                                                            <div className="tw-p-3" key={index}>
                                                                {$t('Quantity {{index}}', { index: index + 1 })}
                                                            </div>
                                                        ))}
                                                </div>
                                                {showBudgetTags && <div className="tw-basis-2/12 tw-p-3">{$t('Budget tags')}</div>}
                                            </div>
                                            <div className="tw-basis-12 tw-shrink-0 tw-p-3"></div>
                                        </div>

                                        <div ref={expensesListRef}>
                                            {'expenses' === activeTab && (
                                                <DragDropContext onDragEnd={handleOnDragEnd}>
                                                    <Droppable droppableId="expenseslist">
                                                        {(provided) => (
                                                            <div className="expenseslist" {...provided.droppableProps} ref={provided.innerRef}>
                                                                {expenses.map((expense, i) => {
                                                                    if (!expense.deleted) {
                                                                        if (editableTable) {
                                                                            return (
                                                                                <Draggable key={i} draggableId={i.toString()} index={i}>
                                                                                    {(provided) => (
                                                                                        <div
                                                                                            ref={provided.innerRef}
                                                                                            {...provided.draggableProps}
                                                                                            {...provided.dragHandleProps}
                                                                                            className={classNames('tw-border-t-2 first:tw-border-t-0', {
                                                                                                'tw-hidden': !showExpense[i],
                                                                                            })}
                                                                                        >
                                                                                            <ExpenseItem
                                                                                                updateExpense={updateExpense}
                                                                                                price={expense.price}
                                                                                                amount={expense.amount}
                                                                                                priceTemp={expense.priceTemp}
                                                                                                expenseKey={i}
                                                                                                expenseCommitmentExists={expense.commitment_exists}
                                                                                                name={expense.name}
                                                                                                description={expense.description}
                                                                                                type={expense.type}
                                                                                                expenseId={expense.id}
                                                                                                isNewExpense={expense.id.toString().indexOf('new-') === 0}
                                                                                                commitmentsExists={commitmentsExists}
                                                                                                maxQuantitiesColumns={maxQuantitiesColumns}
                                                                                                showBudgetTags={showBudgetTags}
                                                                                                budgetTags={budgetTagCells[expense.id]}
                                                                                                onOpenBudgetTags={openBudgetTags}
                                                                                                handleDeleteExpense={handleDeleteExpense}
                                                                                                handleClick={() => handleClick(expense)}
                                                                                                handleAddQuantityColumn={() => handleAddQuantityColumn(i)}
                                                                                                handleDeleteQuantityColumn={(quantityIndex) =>
                                                                                                    handleDeleteQuantityColumn(i, quantityIndex)
                                                                                                }
                                                                                            />
                                                                                        </div>
                                                                                    )}
                                                                                </Draggable>
                                                                            );
                                                                        } else {
                                                                            return (
                                                                                <div key={i} className="tw-border-t-2 first:tw-border-t-0">
                                                                                    <ExpenseItemShow
                                                                                        key={i}
                                                                                        price={expense.price}
                                                                                        amount={expense.amount}
                                                                                        expenseKey={i}
                                                                                        maxQuantitiesColumns={maxQuantitiesColumns}
                                                                                        showBudgetTags={showBudgetTags}
                                                                                        budgetTags={budgetTagCells[expense.id]}
                                                                                        onOpenBudgetTags={openBudgetTags}
                                                                                        expenseId={expense.id}
                                                                                        name={expense.name}
                                                                                        type={expense.type}
                                                                                        description={expense.description}
                                                                                    />
                                                                                </div>
                                                                            );
                                                                        }
                                                                    }
                                                                })}
                                                                {provided.placeholder}
                                                            </div>
                                                        )}
                                                    </Droppable>
                                                </DragDropContext>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className={('assign' === activeTab || !showExpensesTab ? 'active show ' : '') + 'tab-pane fade p-0 stacked-panes border-0'}>
                            <div className="rotateable-table-container running-totals-container">
                                {('assign' === activeTab || !showExpensesTab) && (
                                    <AssignTable
                                        editableTable={editableTable}
                                        totals={totals}
                                        venues={venues}
                                        expenses={expenses}
                                        handleQuantityChange={handleQuantityChange}
                                        tableHeaders={tableHeaders}
                                        handleRotate={handleRotate}
                                        expenseGroups={expenseGroups}
                                        showCommitmentModal={showCommitmentModal}
                                        templateAssignImportUrl={typeof templateAssignImportUrl != 'undefined' ? templateAssignImportUrl : null}
                                        expenseId={props.expenseId}
                                        parentName={props.parentName}
                                        selectable={props.selectable}
                                        toggleFinalState={toggleFinalState}
                                        finalStates={finalStates}
                                        canBeEdited={props.canBeEdited}
                                        forProcurement={props.forProcurement}
                                        quantityLinkStates={quantityLinkStates}
                                        deferredQuantityLinks={deferredQuantityLinks}
                                        openDeferredQuantityPicker={openDeferredQuantityPicker}
                                        clearDeferredQuantityLink={clearDeferredQuantityLink}
                                    ></AssignTable>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <CommitmentModal
                    id="commitment-modal"
                    modal={commitmentModal}
                    committedExpensables={committedExpensables}
                    maxQuantitiesColumns={maxQuantitiesColumns}
                    onClose={closeModal}
                />
                {showBudgetTags && (
                    <BudgetTagsItemModal
                        id="template-item-budget-tags-modal"
                        url={props.budgetTagsUrl}
                        target={budgetTagsTarget}
                        editable={budgetTagsEditable && !!(budgetTagsTarget && budgetTagCells[budgetTagsTarget.id]?.editable)}
                        onClose={closeBudgetTags}
                        onSaved={budgetTagsSaved}
                    />
                )}
            </div>
        );
    }
}

export default TemplateAssign;

if (document.getElementById('TemplateAssign') && !document.getElementById('CommittedExpendituresCreate')) {
    var rootEl = document.getElementById('TemplateAssign'); // data attributes below help us
    ReactDOM.render(
        <App>
            <TemplateAssign
                editable={rootEl.getAttribute('data-editable')}
                expenseId={expense_id}
                parentName={parentName}
                canBeEdited={canBeEdited === undefined ? true : !!canBeEdited}
                forProcurement={false}
                budgetTagsUrl={rootEl.getAttribute('data-budget-tags-url')}
            />
        </App>,
        rootEl,
    );
}
